import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BaseUrl } from '../App';
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';


const SeoForm = () => {
  const [settings, setSettings] = useState({
    googleAnalyticsCode: '',
    pageTitle: '',
    metaDescription: '',
    ogTitle: '',
    ogType: '',
    ogUrl: '',
    ogImage: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  
  const [successMessage, setSuccessMessage] = useState('');
  // Define settings mappings
  const settingsMappings = {
    ct_google_analytics_code: 'googleAnalyticsCode',
    ct_page_title: 'pageTitle',
    ct_seo_meta_description: 'metaDescription',
    ct_seo_og_title: 'ogTitle',
    ct_seo_og_type: 'ogType',
    ct_seo_og_url: 'ogUrl',
    ct_seo_og_image: 'ogImage'
  };

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);
  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${BaseUrl}/backend/api/settings.php`, {
        action: 'get_all_settings',
        api_key: 1
      });
      if (response.data.status === 'true' && response.data.statuscode === 200) {
        const fetchedSettings = {};
        // Map API response to form fields
        response.data.response.forEach(setting => {
          const formField = settingsMappings[setting.option_name];
          if (formField) {
            fetchedSettings[formField] = setting.option_value;
          }
        });
        setSettings(prevSettings => ({
          ...prevSettings,
          ...fetchedSettings
        }));
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (error) {
      setError('Error fetching settings: ' + (error.message || 'Unknown error'));
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (optionName, optionValue) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BaseUrl}/backend/api/settings.php`, {
        action: 'update_setting',
        api_key: 1,
        option_name: optionName,
        option_value: optionValue !== undefined ? optionValue : ''
      });
      if (response.data.status === 'true' && response.data.statuscode === 200) {
        setSuccessMessage(`Updated ${optionName} setting successfully`);
        return true;
      } else {
        throw new Error(response.data.response || 'Update failed');
      }
    } catch (error) {
      console.error(`Error updating ${optionName}:`, error);
      throw error;
    }finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    setLoading(true);
    const file = e.target.files[0];
    if (file) {
      // Create a FormData instance for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('action', 'update_setting');
      // formData.append('api_key', API_KEY);
      formData.append('option_name', 'ct_seo_og_image');
      try {
        const response = await axios.post(formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        if (response.data.status === 'true') {
          setSettings(prev => ({
            ...prev,
            ogImage: file.name
          }));
        } else {
          throw new Error('Failed to upload image');
        }
      } catch (error) {
        setError('Error uploading image: ' + (error.message || 'Unknown error'));
      }finally {
        setLoading(false);
      }
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage('');
    try {
      const updatePromises = Object.entries(settingsMappings).map(([apiKey, formKey]) => 
        updateSetting(apiKey, settings[formKey])
      );
      await Promise.all(updatePromises);
      setSuccessMessage('Settings saved successfully!');
      // Refresh settings from server to ensure we have the latest data
      await fetchSettings();
    } catch (error) {
      setError('Error saving settings: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  if (loading && !Object.values(settings).some(Boolean)) {
    return <div className="text-center py-6">Loading settings...</div>;
  }
  return (
    <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-52 py-6">
      <h1 className="text-2xl font-semibold mb-6 text-center">SEO Settings</h1>
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col">
          <label htmlFor="googleAnalyticsCode" className="font-medium">
            Google Analytics Code
          </label>
          <input
            id="googleAnalyticsCode"
            name="googleAnalyticsCode"
            type="text"
            value={settings.googleAnalyticsCode}
            onChange={handleInputChange}
            className="mt-1 border border-gray-300 rounded-md p-2"
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="pageTitle" className="font-medium">
            Page Title
          </label>
          <input
            id="pageTitle"
            name="pageTitle"
            type="text"
            value={settings.pageTitle}
            onChange={handleInputChange}
            className="mt-1 border border-gray-300 rounded-md p-2"
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="metaDescription" className="font-medium">
            Meta Description
          </label>
          <textarea
            id="metaDescription"
            name="metaDescription"
            value={settings.metaDescription}
            onChange={handleInputChange}
            className="mt-1 border border-gray-300 rounded-md p-2"
            rows="3" />
        </div>
        <div className="flex flex-col">
          <label htmlFor="ogTitle" className="font-medium">
            OG Title
          </label>
          <input
            id="ogTitle"
            name="ogTitle"
            type="text"
            value={settings.ogTitle}
            onChange={handleInputChange}
            className="mt-1 border border-gray-300 rounded-md p-2" />
        </div>
        <div className="flex flex-col">
          <label htmlFor="ogType" className="font-medium">
            OG Type
          </label>
          <input
            id="ogType"
            name="ogType"
            type="text"
            value={settings.ogType}
            onChange={handleInputChange}
            className="mt-1 border border-gray-300 rounded-md p-2"  />
        </div>
        <div className="flex flex-col">
          <label htmlFor="ogUrl" className="font-medium">
            OG URL
          </label>
          <input
            id="ogUrl"
            name="ogUrl"
            type="text"
            value={settings.ogUrl}
            onChange={handleInputChange}
            className="mt-1 border border-gray-300 rounded-md p-2" />
        </div>
        <div className="flex flex-col">
          <label htmlFor="ogImage" className="font-medium">
            OG Image
          </label>
          <div className="mt-2">
            <input
              id="ogImage"
              name="ogImage"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="border border-gray-300 p-2 rounded-md"
            />
            {settings.ogImage && (
              <p className="mt-2 text-sm text-gray-600">
                Current image: {settings.ogImage}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-center mt-6">
          <button
            type="submit"
            disabled={loading}
            className={`${
              loading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
            } text-white px-6 py-2 rounded-md`}
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SeoForm;

// import axios from 'axios';

// const SeoForm = () => {
//   const [settings, setSettings] = useState({
//     googleAnalyticsCode: '',
//     pageTitle: '',
//     metaDescription: '',
//     ogTitle: '',
//     ogType: '',
//     ogUrl: '',
//     ogImage: null
//   });
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [successMessage, setSuccessMessage] = useState('');
  
//   // Define settings mappings
//   const settingsMappings = {
//     ct_google_analytics_code: 'googleAnalyticsCode',
//     ct_page_title: 'pageTitle',
//     ct_seo_meta_description: 'metaDescription',
//     ct_seo_og_title: 'ogTitle',
//     ct_seo_og_type: 'ogType',
//     ct_seo_og_url: 'ogUrl',
//     ct_seo_og_image: 'ogImage'
//   };

//   const fetchSettings = async () => {
//     setLoading(true);
//     setError(null);

//     try {
//       const response = await axios.post('http://10.27.1.112:80/megaclean/backend/api/settings.php', {
//         action: 'get_all_settings',
//         api_key: 1
//       });

//       if (response.data.status === 'true' && response.data.statuscode === 200) {
//         const fetchedSettings = {};
        
//         // Map API response to form fields
//         response.data.response.forEach(setting => {
//           const formField = settingsMappings[setting.option_name];
//           if (formField) {
//             fetchedSettings[formField] = setting.option_value;
//           }
//         });


//         setSettings(prevSettings => ({
//           ...prevSettings,
//           ...fetchedSettings
//         }));
//       } else {
//         throw new Error('Failed to fetch settings');
//       }
//     } catch (error) {
//       setError('Error fetching settings: ' + (error.message || 'Unknown error'));
//       console.error('Error fetching settings:', error);
//     } finally {
//       setLoading(false);
//     }
//   };


//   const updateSetting = async (optionName, optionValue) => {
//     try {
//       const response = await axios.post('http://10.27.1.112:80/megaclean/backend/api/settings.php', {
//         action: 'update_setting',
//         api_key: 1,
//         option_name: optionName,
//         option_value: optionValue !== undefined ? optionValue : ''
//       });

//       if (response.data.status === 'true' && response.data.statuscode === 200) {
//         return true;
//       } else {
//         throw new Error(response.data.response || 'Update failed');
//       }
//     } catch (error) {
//       console.error(`Error updating ${optionName}:`, error);
//       throw error;
//     }
//   };

//   useEffect(() => {
//     fetchSettings();
//   }, []);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setSettings(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   const handleFileChange = async (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       // Create a FormData instance for file upload
//       const formData = new FormData();
//       formData.append('file', file);
//       formData.append('action', 'update_setting');
//       // formData.append('api_key', API_KEY);
//       formData.append('option_name', 'ct_seo_og_image');

//       try {
//         const response = await axios.post(formData, {
//           headers: {
//             'Content-Type': 'multipart/form-data'
//           }
//         });

//         if (response.data.status === 'true') {
//           setSettings(prev => ({
//             ...prev,
//             ogImage: file.name
//           }));
//         } else {
//           throw new Error('Failed to upload image');
//         }
//       } catch (error) {
//         setError('Error uploading image: ' + (error.message || 'Unknown error'));
//       }
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setError(null);
//     setSuccessMessage('');

//     try {
//       const updatePromises = Object.entries(settingsMappings).map(([apiKey, formKey]) => 
//         updateSetting(apiKey, settings[formKey])
//       );

//       await Promise.all(updatePromises);
//       setSuccessMessage('Settings saved successfully!');
      
//       // Refresh settings from server to ensure we have the latest data
//       await fetchSettings();
//     } catch (error) {
//       setError('Error saving settings: ' + (error.message || 'Unknown error'));
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (loading && !Object.values(settings).some(Boolean)) {
//     return <div className="text-center py-6">Loading settings...</div>;
//   }

//   return (
//     <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-52 py-6">
//       <h1 className="text-2xl font-semibold mb-6 text-center">SEO Settings</h1>
      
//       {error && (
//         <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
//           {error}
//         </div>
//       )}
      
//       {successMessage && (
//         <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
//           {successMessage}
//         </div>
//       )}

//       <form onSubmit={handleSubmit} className="space-y-4">
//         <div className="flex flex-col">
//           <label htmlFor="googleAnalyticsCode" className="font-medium">
//             Google Analytics Code
//           </label>
//           <input
//             id="googleAnalyticsCode"
//             name="googleAnalyticsCode"
//             type="text"
//             value={settings.googleAnalyticsCode}
//             onChange={handleInputChange}
//             className="mt-1 border border-gray-300 rounded-md p-2"
//           />
//         </div>

//         <div className="flex flex-col">
//           <label htmlFor="pageTitle" className="font-medium">
//             Page Title
//           </label>
//           <input
//             id="pageTitle"
//             name="pageTitle"
//             type="text"
//             value={settings.pageTitle}
//             onChange={handleInputChange}
//             className="mt-1 border border-gray-300 rounded-md p-2"
//           />
//         </div>

//         <div className="flex flex-col">
//           <label htmlFor="metaDescription" className="font-medium">
//             Meta Description
//           </label>
//           <textarea
//             id="metaDescription"
//             name="metaDescription"
//             value={settings.metaDescription}
//             onChange={handleInputChange}
//             className="mt-1 border border-gray-300 rounded-md p-2"
//             rows="3"
//           />
//         </div>

//         <div className="flex flex-col">
//           <label htmlFor="ogTitle" className="font-medium">
//             OG Title
//           </label>
//           <input
//             id="ogTitle"
//             name="ogTitle"
//             type="text"
//             value={settings.ogTitle}
//             onChange={handleInputChange}
//             className="mt-1 border border-gray-300 rounded-md p-2"
//           />
//         </div>

//         <div className="flex flex-col">
//           <label htmlFor="ogType" className="font-medium">
//             OG Type
//           </label>
//           <input
//             id="ogType"
//             name="ogType"
//             type="text"
//             value={settings.ogType}
//             onChange={handleInputChange}
//             className="mt-1 border border-gray-300 rounded-md p-2"
//           />
//         </div>

//         <div className="flex flex-col">
//           <label htmlFor="ogUrl" className="font-medium">
//             OG URL
//           </label>
//           <input
//             id="ogUrl"
//             name="ogUrl"
//             type="text"
//             value={settings.ogUrl}
//             onChange={handleInputChange}
//             className="mt-1 border border-gray-300 rounded-md p-2"
//           />
//         </div>

//         <div className="flex flex-col">
//           <label htmlFor="ogImage" className="font-medium">
//             OG Image
//           </label>
//           <div className="mt-2">
//             <input
//               id="ogImage"
//               name="ogImage"
//               type="file"
//               accept="image/*"
//               onChange={handleFileChange}
//               className="border border-gray-300 p-2 rounded-md"
//             />
//             {settings.ogImage && (
//               <p className="mt-2 text-sm text-gray-600">
//                 Current image: {settings.ogImage}
//               </p>
//             )}
//           </div>
//         </div>

//         <div className="flex justify-center mt-6">
//           <button
//             type="submit"
//             disabled={loading}
//             className={`${
//               loading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
//             } text-white px-6 py-2 rounded-md`}
//           >
//             {loading ? 'Saving...' : 'Save Settings'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default SeoForm;